// Section « Termes les plus recherchés » de la page /search-metrics : le top des
// termes tapés par les visiteurs sur la fenêtre glissante de sept jours.
//
// Périmètre de conservation, affiché en clair dans la section : termes normalisés
// agrégés par jour, cardinalité plafonnée, purge au-delà de sept jours ; jamais
// d'adresse de visiteur, jamais une requête individuelle.
import React from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import type { SearchTermPoint } from "../hooks/useSearchMetrics";

const nombre = new Intl.NumberFormat("fr-FR");

/** Les barres du graphique : au-delà, les libellés deviennent illisibles. */
const BARRES = 15;

interface SearchTermsSectionProps {
	terms: SearchTermPoint[];
	windowDays: number;
	loading: boolean;
	error: string | null;
}

const SearchTermsSection: React.FC<SearchTermsSectionProps> = ({ terms, windowDays, loading, error }) => {
	const total = terms.reduce((n, t) => n + t.count, 0);
	const meilleurs = terms.slice(0, BARRES);

	const optionsBarres: ApexOptions = {
		chart: { type: "bar", height: 420, toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "62%" } },
		dataLabels: { enabled: false },
		grid: { borderColor: "#e2e8f0", strokeDashArray: 3 },
		xaxis: {
			categories: meilleurs.map((t) => t.term),
			labels: { style: { colors: "#94a3b8" }, formatter: (v) => nombre.format(Number(v)) },
			axisBorder: { color: "#e2e8f0" },
			axisTicks: { color: "#e2e8f0" },
		},
		yaxis: { labels: { style: { colors: "#64748b" }, maxWidth: 260 } },
		tooltip: { y: { formatter: (v) => `${nombre.format(v)} recherche(s)` } },
		colors: ["#0ea5e9"],
	};

	return (
		<section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
			<header>
				<p className="text-sm font-medium uppercase tracking-wide text-slate-500">
					Termes les plus recherchés ({windowDays} derniers jours)
				</p>
				<p className="mt-1 text-xs text-slate-400">
					Termes normalisés et agrégés par jour, plafonnés en nombre et purgés après {windowDays} jours.
					Aucune adresse de visiteur, aucune requête individuelle n'est conservée.
				</p>
			</header>

			{error ? (
				<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
			) : null}

			{loading && terms.length === 0 ? (
				<div className="h-64 animate-pulse rounded-2xl border border-slate-100 bg-slate-50" />
			) : terms.length === 0 ? (
				<div className="rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-6 text-sm text-slate-500">
					Aucune recherche enregistrée sur la fenêtre. Les termes apparaissent ici au fil des
					recherches des visiteurs, au plus tard vingt secondes après.
				</div>
			) : (
				<div className="grid gap-6 xl:grid-cols-2">
					<div className="rounded-2xl border border-slate-100 p-4">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
							Top {meilleurs.length} en volume
						</p>
						<Chart
							options={optionsBarres}
							series={[{ name: "Recherches", data: meilleurs.map((t) => t.count) }]}
							type="bar"
							height={420}
						/>
					</div>
					<div className="rounded-2xl border border-slate-100 p-4">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
							Classement complet (top {terms.length})
						</p>
						<div className="mt-2 max-h-[420px] overflow-y-auto">
							<table className="w-full text-sm">
								<thead className="sticky top-0 bg-white text-left text-xs uppercase tracking-wide text-slate-400">
									<tr>
										<th className="py-1.5 pr-2 font-semibold">#</th>
										<th className="py-1.5 pr-2 font-semibold">Terme</th>
										<th className="py-1.5 pr-2 text-right font-semibold">Volume</th>
										<th className="py-1.5 text-right font-semibold">Part</th>
									</tr>
								</thead>
								<tbody>
									{terms.map((t, i) => (
										<tr key={t.term} className="border-t border-slate-50">
											<td className="py-1.5 pr-2 tabular-nums text-slate-400">{i + 1}</td>
											<td className="py-1.5 pr-2 text-slate-700">
												{t.term}
												{t.zeroResults > 0 ? (
													<span className="ml-2 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
														{nombre.format(t.zeroResults)} sans résultat
													</span>
												) : null}
											</td>
											<td className="py-1.5 pr-2 text-right tabular-nums font-semibold text-slate-900">
												{nombre.format(t.count)}
											</td>
											<td className="py-1.5 text-right tabular-nums text-slate-400">
												{total > 0 ? `${((t.count / total) * 100).toFixed(1).replace(".", ",")} %` : "-"}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}
		</section>
	);
};

export default SearchTermsSection;
